import { useQuery } from '@apollo/client';
import React from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { RouteComponentProps, withRouter } from 'react-router';
import { useLocation } from 'react-router-dom';
import { DNS_MAP } from '../config';
import type { AdminTheme } from '../theme';
import { UserRole } from '../__gqltypes__';
import { KEY_TO_NAME } from './config';
import { DEPLOYMENT_INFO_QUERY, DeploymentInfoResult } from './queries';

export type DisplayedProfile = {
  role?: string;
  photoURL?: string | null;
  displayName: string | null;
  picture?: string | null;
};

type Props = {
  app: keyof typeof KEY_TO_NAME;
  visible: boolean;
  profile: DisplayedProfile;
  onSidebarToggle: () => void;
  expanded: boolean;
  getPageNameFromRoute: (route: string) => string;
  role: UserRole;
  theme: AdminTheme;
  onThemeChange: (theme: AdminTheme) => void;
};

const Topbar = ({
  app,
  visible,
  profile,
  onSidebarToggle,
  expanded,
  role,
  getPageNameFromRoute,
  theme,
  onThemeChange,
}: RouteComponentProps & Props) => {
  const version = (process.env.REACT_APP_NAMESPACE || '').toUpperCase();
  const location = useLocation();
  // Errors silently here on purpose: an admin backend deployed before this field existed
  // (or a local backend run outside the Jenkins pipeline) just returns a GraphQL error for
  // the unknown field — `data` stays undefined and the badge below simply doesn't render,
  // rather than breaking the whole topbar.
  const { data: deploymentData } = useQuery<DeploymentInfoResult>(DEPLOYMENT_INFO_QUERY, {
    errorPolicy: 'ignore',
  });
  const deploymentInfo = deploymentData?.deploymentInfo;

  const handleToggle = () => onSidebarToggle();
  const isDarkMode = theme === 'dark';
  const handleThemeChange = () => onThemeChange(isDarkMode ? 'light' : 'dark');
  const themeSwitchLabel = isDarkMode ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <>
      <Navbar
        variant={isDarkMode ? 'dark' : 'light'}
        className="navbar-expand topbar mb-0 fixed-top shadow admin-topbar"
      >
        <Nav>
          <Nav.Item>
            <div className="d-flex align-items-center">
              <div
                role="button"
                tabIndex={0}
                className={`rounded mr-3 p-1 pl-2 pr-2 text-center admin-sidebar-toggle${
                  expanded ? ' admin-sidebar-toggle-expanded' : ''
                }`}
                onClick={handleToggle}>
                <i className="fa fa-bars" />
              </div>
              {getPageNameFromRoute(window.location.pathname)}
            </div>
          </Nav.Item>
        </Nav>
        <Nav className="ml-auto align-items-center">
          {(process.env.REACT_APP_COMPILE_TIMESTAMP || process.env.REACT_APP_FE_BRANCH || deploymentInfo) && (
            <div className="mx-2 text-muted text-xs d-none d-sm-flex flex-column text-center">
              {process.env.REACT_APP_COMPILE_TIMESTAMP && (
                <span>Last update: {new Date(process.env.REACT_APP_COMPILE_TIMESTAMP).toLocaleString()}</span>
              )}
              {process.env.REACT_APP_FE_BRANCH && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="fe-deployment-info-tooltip">
                      FE branch: {process.env.REACT_APP_FE_BRANCH}
                      <br />
                      commit: {process.env.REACT_APP_FE_COMMIT}
                      <br />
                      build: #{process.env.REACT_APP_FE_BUILD}
                      <br />
                      source: {process.env.REACT_APP_FE_SOURCE}
                    </Tooltip>
                  }
                >
                  <span>
                    Web admin revision: {process.env.REACT_APP_FE_BRANCH}@{(process.env.REACT_APP_FE_COMMIT || '').slice(0, 7)} (
                    {process.env.REACT_APP_FE_SOURCE})
                  </span>
                </OverlayTrigger>
              )}
              {deploymentInfo && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="deployment-info-tooltip">
                      BE branch: {deploymentInfo.branch}
                      <br />
                      commit: {deploymentInfo.commit}
                      <br />
                      build: #{deploymentInfo.build}
                    </Tooltip>
                  }
                >
                  <span>
                    Backend revision: {deploymentInfo.branch}@{deploymentInfo.commit.slice(0, 7)}
                  </span>
                </OverlayTrigger>
              )}
            </div>
          )}
          <Nav.Item>
            {visible ? (
              <DropdownButton
                id="dropdown-basic-button"
                title={version}
                defaultValue={version}
                disabled={role === UserRole.CONTENT_MODERATOR}
                variant={isDarkMode ? 'outline-light' : 'outline-secondary'}>
                {Object.entries(DNS_MAP).map((key) => (
                  <Dropdown.Item
                    key={key.toString()}
                    href={`http://${key[1]}${location.pathname}${location.search}${location.hash}`}
                  >
                    {key[0].toUpperCase()}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            ) : (
              <span>{app}</span>
            )}
          </Nav.Item>
          <Nav.Item className="px-2 d-flex align-items-center">
            <Form.Check
              type="switch"
              id="admin-theme-switch"
              label={
                <>
                  <span className="sr-only">{themeSwitchLabel}</span>
                  <span className="admin-theme-switch-icon" aria-hidden="true">
                    <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`} />
                  </span>
                </>
              }
              checked={isDarkMode}
              onChange={handleThemeChange}
              title={themeSwitchLabel}
              className="mb-0 admin-theme-switch"
            />
          </Nav.Item>
          <div className="topbar-divider d-none d-sm-block" />
          <Nav.Item>
            <span>{app}</span>
          </Nav.Item>
          <div className="topbar-divider d-none d-sm-block" />

          <Nav.Item>
            <Nav.Link>
              <div className="d-lg-inline">
                <span className="mr-2 d-block text-gray-600 small">{profile.displayName}</span>
                <span className="mr-2 d-block text-gray-600 small">{profile.role}</span>
              </div>
              <Image roundedCircle className="img-profile" src={profile.picture || profile.photoURL || ''} />
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </Navbar>
    </>
  );
};

export default withRouter(Topbar);
